import { ArrowLeft, ArrowRight, Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { DialogClose } from '@/components/ui/dialog'
import { cn } from '@/utils/cn'
import type { FeatureSlide } from './slides'

function SlideIndicators({
  slides,
  index,
  onSelect,
}: {
  slides: readonly FeatureSlide[]
  index: number
  onSelect: (index: number) => void
}) {
  const { t } = useTranslation()
  return (
    <div
      role="group"
      className="flex flex-wrap items-center justify-center"
      aria-label={t('whatsNew.chooseSlide')}
    >
      {slides.map((slide, position) => (
        <Button
          key={slide.id}
          variant="ghost"
          size="icon-sm"
          className="rounded-full max-md:w-7"
          aria-label={t('whatsNew.goToSlide', { feature: t(slide.labelKey) })}
          aria-current={position === index ? 'step' : undefined}
          onClick={() => onSelect(position)}
        >
          <span
            aria-hidden
            className={cn(
              'h-1.5 rounded-full transition-all motion-reduce:transition-none',
              position === index ? 'w-5 bg-primary' : 'w-1.5 bg-muted-foreground/30'
            )}
          />
        </Button>
      ))}
    </div>
  )
}

function TourDismiss({ last }: { last: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="mt-3 flex flex-col-reverse items-stretch gap-1.5 border-t border-border/60 pt-3 sm:mt-4 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:pt-4">
      <p className="text-center text-xs text-muted-foreground sm:text-left">
        {t('whatsNew.reopen')}
      </p>
      <DialogClose asChild>
        <Button
          variant={last ? 'solid' : 'ghost'}
          className={cn(
            'shrink-0',
            last &&
              'bg-foreground px-6 text-background shadow-md shadow-primary/10 hover:bg-foreground/90'
          )}
        >
          {last && <Check className="size-4" aria-hidden />}
          {t(last ? 'whatsNew.done' : 'whatsNew.later')}
        </Button>
      </DialogClose>
    </div>
  )
}

export function FeatureCarouselNavigation({
  slides,
  index,
  onSelect,
}: {
  slides: readonly FeatureSlide[]
  index: number
  onSelect: (index: number) => void
}) {
  const { t } = useTranslation()
  const last = index === slides.length - 1
  return (
    <div className="shrink-0 px-6 pb-4 pt-3 sm:px-8 sm:pb-6 sm:pt-5">
      <div className="flex items-center justify-between gap-2">
        <Button
          size="icon"
          variant="outline"
          className="shrink-0 rounded-full"
          disabled={index === 0}
          onClick={() => onSelect(index - 1)}
          aria-label={t('whatsNew.previous')}
        >
          <ArrowLeft className="size-4" aria-hidden />
        </Button>
        <SlideIndicators slides={slides} index={index} onSelect={onSelect} />
        <Button
          size="icon"
          variant="outline"
          className="shrink-0 rounded-full"
          disabled={last}
          onClick={() => onSelect(index + 1)}
          aria-label={t('whatsNew.next')}
        >
          <ArrowRight className="size-4" aria-hidden />
        </Button>
      </div>
      <TourDismiss last={last} />
    </div>
  )
}
